import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchSliders,
  createSliderThunk,
  updateSliderThunk,
  deleteSliderThunk,
} from "../../features/auth/store/sliderSlice";
import { SliderList } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";
import { SliderItem } from "../../features/auth/store/types/slider-types";

const SlidersManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: SliderItem[] = useSelector(
    (state: any) => state.sliders?.items ?? []
  );
  const loading: boolean = useSelector(
    (state: any) => state.sliders?.loading ?? false
  );
  const saving: boolean = useSelector(
    (state: any) => state.sliders?.saving ?? false
  );
  const error: string | null = useSelector(
    (state: any) => state.sliders?.error ?? null
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SliderItem | null>(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    dispatch(fetchSliders());
  }, [dispatch]);

  useEffect(() => {
    if (editing) {
      setTitle(editing.title ?? "");
      setUrl(editing.url ?? "");
      setFile(null);
    } else {
      setTitle("");
      setUrl("");
      setFile(null);
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: SliderItem) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: SliderItem) => {
    if (!window.confirm("Delete this slider?")) return;
    dispatch(deleteSliderThunk(item.id));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (editing) {
      dispatch(
        updateSliderThunk({
          id: editing.id,
          title,
          url,
          image: file ?? null,
        })
      );
    } else {
      dispatch(
        createSliderThunk({
          title,
          url,
          image: file ?? null,
        })
      );
    }
    setFormOpen(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Sliders</h2>
        <div className="flex gap-2">
          <OutlineButton onClick={openCreate} content="New Slider" />
          <GradientButton
            onClick={() => dispatch(fetchSliders())}
            content="Refresh"
          />
        </div>
      </div>

      <div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <SliderList items={items} onEdit={openEdit} onDelete={handleDelete} />
        )}
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      </div>

      {formOpen && (
        <ModalContainer
          // onClose={() => setFormOpen(false)}
          title={editing ? "Edit Slider" : "Create Slider"}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {editing?.image_url && !file && (
                <div className="mt-2">
                  <img
                    src={editing.image_url}
                    alt="preview"
                    className="w-48 h-auto rounded"
                  />
                </div>
              )}
              {file && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="w-48 h-auto rounded"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />

              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default SlidersManagementPage;
